import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Container from "@mui/material/Container";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import DI from "../utility/DI";
import Footer from "../utility/Footer";
import SettingsIcon from "@mui/icons-material/Settings";
import RefreshIcon from "@mui/icons-material/Refresh";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Avatar, Chip, Tooltip, Typography } from "@mui/material";
import AppShortcutIcon from "@mui/icons-material/AppShortcut";
import Navigators from "../Navigators";
import { environment } from "../../Env_data/environment";
import { AppContext } from "../../App";
import GuidedTour from "../utility/GuidedTour";

export const FetchContext = React.createContext(false);
function stringToColor(number) {
  let hash = 0;

  /* eslint-disable no-bitwise */
  hash = number & 0xffffffff;
  hash = ((hash >> 16) ^ hash) * 0x45d9f3b;
  hash = ((hash >> 16) ^ hash) * 0x45d9f3b;
  hash = (hash >> 16) ^ hash;
  /* eslint-enable no-bitwise */

  let color = "#";

  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
}

function stringAvatar(name) {
  return {
    sx: {
      bgcolor: stringToColor(name),
    },
    children: `${name}`,
  };
}
function ResponsiveAppBar(props) {
  const {
    mode,
    setMode,
    banner,
    hideBanner,
    di: { session, logout, success, GET, POST, urls, error, info },
  } = props;
  const [refresh, setRefresh] = React.useState(false);
  const navigate = useNavigate();
  const { APP_NAME } = environment;
  const { state } = React.useContext(AppContext)

  // Guided Tour state
  const tourKey = `${APP_NAME}_tour_done_${session?.id}`;
  const [runTour, setRunTour] = React.useState(false);

  React.useEffect(() => {
    if (session?.id && !localStorage.getItem(tourKey)) {
      // Delay tour start to let the page render fully
      const timer = setTimeout(() => setRunTour(true), 800);
      return () => clearTimeout(timer);
    }
  }, [session?.id, tourKey]);

  const handleTourFinish = React.useCallback(() => {
    setRunTour(false);
    if (session?.id) {
      localStorage.setItem(tourKey, "true");
    }
  }, [session?.id, tourKey]);

  const handleReplayTour = React.useCallback(() => {
    setRunTour(true);
  }, []);

  const location = useLocation();
  const isAiPage = location.pathname === '/panel/ai';
  const [friendsCount, setFriendsCount] = React.useState(null);

  React.useEffect(() => {
    if (session?.id) {
      const isMainPath = ["/panel", "/panel/", "/home", "/home/"].includes(location.pathname);

      if (friendsCount === null || isMainPath) {
        POST(urls.get.get_friends)
          .then((res) => {
            if (res?.success) {
              const count = res.data?.length ?? 0;
              setFriendsCount(count);
              // Only auto-redirect on very first load (friendsCount was null),
              // not when the user explicitly navigates to /panel themselves.
              if (count === 0 && friendsCount === null && isMainPath) {
                navigate("/panel/friends");
              } else if (count === 0 && isMainPath) {
                // User intentionally navigated to Shared Expenses — show a blue info hint
                info("Add at least one friend to split.");
              }
            }
          })
          .catch((err) => {
            console.error("Failed to check friends status:", err);
          });
      }
    }
  }, [session?.id, location.pathname, refresh, POST, urls.get.get_friends, navigate]);


  React.useEffect(() => {
    GET(urls.get.input_suggestions)
      .then((res) => {
        if (res.success) {
          if (session && Object.keys(session).length > 0)
            localStorage.setItem(APP_NAME + '_suggestions', JSON.stringify(res.suggestions));
        } else {
          error(res.message);
        }
      })
      .catch((err) => {
        error("Failed to fetch suggestions");
      });
  }, []);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };
  const displayName = session?.firstname
    ? `${session.firstname}`
    : (session?.name || (session?.id ? `User ${session.id}` : ""));

  return (
    <FetchContext.Provider value={{ refresh, setRefresh }}>
      <AppBar position="sticky">
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ px: { xs: 1.5, sm: 2 } }}>
            {/* Brand Logo (Visible on both Mobile and Desktop) — Clicks navigate to /panel/about */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mr: { xs: 1, sm: 2 },
                cursor: "pointer",
                flexShrink: 0,
                transition: "opacity 0.2s ease, transform 0.2s ease",
                "&:active": { transform: "scale(0.96)" },
                "&:hover": { opacity: 0.9 },
              }}
              onClick={() => navigate("/panel/about")}
              title="About Spent App"
            >
              <Box
                component="img"
                src="/white_logo.png"
                alt="Spent App"
                loading="lazy"
                sx={{
                  height: { xs: 24, sm: 28, md: 32 },
                  width: "auto",
                  display: "block",
                  objectFit: "contain",
                }}
              />
            </Box>

            {/* Mobile Header: Avatar + User Greeting */}
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                alignItems: "center",
                gap: 0.8,
                flexGrow: 1,
                minWidth: 0,
                cursor: "pointer",
              }}
              onClick={() => navigate("/panel/manage")}
            >
              {session?.id && (
                <Avatar
                  {...stringAvatar(session.id)}
                  sx={{
                    ...stringAvatar(session.id).sx,
                    width: 30,
                    height: 30,
                    fontSize: "0.8rem",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
                    flexShrink: 0,
                  }}
                />
              )}
              <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                <Typography
                  variant="caption"
                  noWrap
                  sx={{
                    color: "rgba(255,255,255,0.75)",
                    fontSize: "0.65rem",
                    display: "block",
                    lineHeight: 1.1,
                  }}
                >
                  {getGreeting()} 👋
                </Typography>
                <Typography
                  variant="subtitle2"
                  noWrap
                  sx={{
                    color: "#ffffff",
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    lineHeight: 1.2,
                  }}
                >
                  {displayName || "Finance Hub"}
                </Typography>
              </Box>
            </Box>

            {/* Desktop Avatar separator */}
            <Box
              sx={{
                flexGrow: 1,
                display: { xs: "none", md: "flex" },
                borderLeft: "2px solid rgba(255,255,255,0.3)",
                paddingLeft: "20px",
                alignItems: "center",
              }}
            >
              {session?.id && <Avatar {...stringAvatar(session.id)} />}
            </Box>

            {/* Actions: Settings gear (mobile+desktop), refresh, notifications, guide */}
            <Box sx={{ display: "flex", alignItems: "center", gap: { xs: "2px", sm: "6px" } }}>
              <Tooltip title="App Guide" arrow>
                <IconButton
                  onClick={handleReplayTour}
                  color="inherit"
                  sx={{ display: { xs: "none", sm: "inline-flex" } }}
                >
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>

              <IconButton
                style={{ transform: refresh ? "rotate(180deg)" : "" }}
                onClick={() => setRefresh((prev) => !prev)}
                color="inherit"
                sx={{ p: { xs: 0.6, sm: 1 } }}
              >
                <RefreshIcon sx={{ color: "white", fontSize: { xs: 20, sm: 24 } }} />
              </IconButton>

              {/* Settings Gear Icon — visible on both mobile and desktop */}
              <Tooltip title="Settings" arrow>
                <IconButton
                  onClick={() => navigate("/panel/settings")}
                  color="inherit"
                  sx={{
                    p: { xs: 0.6, sm: 1 },
                    color: location.pathname === "/panel/settings" ? "#ffd54f" : "white",
                  }}
                >
                  <SettingsIcon sx={{ fontSize: { xs: 20, sm: 22 } }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Notifications" arrow>
                <IconButton
                  onClick={() => navigate("/panel/notifications")}
                  color="inherit"
                  sx={{ p: { xs: 0.6, sm: 1 } }}
                >
                  <NotificationsIcon sx={{ color: "white", fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          pb: isAiPage ? 0 : { xs: 12, md: 4 },
        }}
      >
        <div style={{ maxWidth: "100vw" }}>
          <Container maxWidth="lg" sx={{ mt: { xs: 0.5, md: 1 }, mb: isAiPage ? 0 : { xs: 8, md: 4 }, px: { xs: 1, sm: 2 } }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                minHeight: isAiPage ? "auto" : "calc(100vh - 160px)",
                gap: '15px'
              }}
            >
              {banner && (
                <Chip
                  color="primary"
                  onDelete={hideBanner}
                  variant="outlined"
                  label="Add to Home Screen this page from browser settings."
                  icon={<AppShortcutIcon />}
                />
              )}
              <div>
                <Navigators />
                <Outlet />
              </div>
              {!isAiPage && <Footer />}
            </div>
          </Container>
        </div>
        <GuidedTour run={runTour} onFinish={handleTourFinish} />
      </Box>
    </FetchContext.Provider>
  );
}

export default DI(ResponsiveAppBar);
